import { logger } from '@/lib/logger';
import { IS_MOBILE_APP } from '@/constants/env';
import { store } from '@/state/store';
import { setCurrentConnectedPrinter, clearCurrentConnectedPrinter } from '@/state/slices/userPreferenceSlice';

interface PrinterConnectionInfo {
    address: string;
    name: string;
    lastConnected: string; // ISO date string
}

interface PrinterSettings {
    autoReconnectEnabled: boolean;
    lastConnectedPrinter?: PrinterConnectionInfo;
}

const STORAGE_KEYS = {
    PRINTER_SETTINGS: 'kirana360_printer_settings',
    LAST_PRINTER: 'kirana360_last_printer',
} as const;

class PersistentPrinterService {
    private reconnectionAttempts = 0;
    private maxReconnectionAttempts = 3;
    private reconnectionDelay = 2000; // 2 seconds

    /**
     * Save data to localStorage
     */
    private setItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            logger.error('Failed to save to localStorage', { error: String(error) });
        }
    }

    /**
     * Get data from localStorage
     */
    private getItem(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            logger.error('Failed to get from localStorage', { error: String(error) });
        }
        return null;
    }

    /**
     * Remove data from localStorage
     */
    private removeItem(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            logger.error('Failed to remove from localStorage', { error: String(error) });
        }
    }

    /**
     * Save printer connection info to localStorage
     */
    savePrinterConnection(address: string, name: string): void {
        try {
            logger.info('Saving printer connection to localStorage', { address, name });

            const connectionInfo: PrinterConnectionInfo = {
                address,
                name,
                lastConnected: new Date().toISOString()
            };

            this.setItem(STORAGE_KEYS.LAST_PRINTER, JSON.stringify(connectionInfo));
            logger.info('Printer connection saved successfully');
        } catch (error) {
            logger.error('Failed to save printer connection', { error: String(error) });
        }
    }

    /**
     * Get saved printer connection info from localStorage
     */
    getSavedPrinterConnection(): PrinterConnectionInfo | null {
        logger.debug('getSavedPrinterConnection', { component: 'PersistentPrinterService' });
        try {
            const stored = this.getItem(STORAGE_KEYS.LAST_PRINTER);

            if (!stored) {
                return null;
            }

            const connectionInfo: PrinterConnectionInfo = JSON.parse(stored);

            // Validate the stored data
            if (connectionInfo.address && connectionInfo.name) {
                return connectionInfo;
            }

            return null;
        } catch (error) {
            logger.error('Failed to get saved printer connection', { error: String(error) });
            return null;
        }
    }

    /**
     * Get printer settings from localStorage
     */
    getPrinterSettings(): PrinterSettings {
        try {
            const stored = this.getItem(STORAGE_KEYS.PRINTER_SETTINGS);

            if (!stored) {
                // Return default settings
                return {
                    autoReconnectEnabled: true
                };
            }

            const settings: PrinterSettings = JSON.parse(stored);
            return {
                ...settings,
                autoReconnectEnabled: settings.autoReconnectEnabled ?? true
            };
        } catch (error) {
            logger.error('Failed to get printer settings', { error: String(error) });
            return { autoReconnectEnabled: true };
        }
    }

    /**
     * Save printer settings to localStorage
     */
    savePrinterSettings(settings: Partial<PrinterSettings>): void {
        try {
            const currentSettings = this.getPrinterSettings();
            const updatedSettings = { ...currentSettings, ...settings };

            this.setItem(STORAGE_KEYS.PRINTER_SETTINGS, JSON.stringify(updatedSettings));
            logger.info('Printer settings saved successfully');
        } catch (error) {
            logger.error('Failed to save printer settings', { error: String(error) });
        }
    }

    /**
     * Check if auto-reconnection should be attempted
     */
    shouldAttemptAutoReconnection(): boolean {
        logger.debug('shouldAttemptAutoReconnection', { component: 'PersistentPrinterService' });
        if (!IS_MOBILE_APP) {
            return false;
        }

        const savedPrinter = this.getSavedPrinterConnection();
        return savedPrinter !== null;
    }

    /**
     * Attempt auto-reconnection to saved printer
     */
    async attemptAutoReconnection(): Promise<boolean> {
        try {
            const savedPrinter = this.getSavedPrinterConnection();
            if (!savedPrinter) {
                logger.debug('No saved printer connection to reconnect to');
                return false;
            }

            logger.info('Attempting auto-reconnection to saved printer', { savedPrinter });

            // Try unified printer service first
            const unifiedPrinterService = await this.getUnifiedPrinterService();
            if (unifiedPrinterService) {
                try {
                    logger.debug('Attempting unified printer service reconnection');
                    await unifiedPrinterService.initialize();
                    logger.debug('Unified printer service initialized successfully');

                    // Create a BluetoothDevice object for unified printer service
                    const deviceObject = {
                        id: savedPrinter.address,
                        name: savedPrinter.name,
                        address: savedPrinter.address,
                        class: 0 // Default class value
                    };

                    logger.debug('Attempting to connect to unified printer', { device: deviceObject });
                    await unifiedPrinterService.connectToPrinter(deviceObject);
                    const isConnected = unifiedPrinterService.isConnected();
                    if (isConnected) {
                        logger.info('Auto-reconnection successful (unified printer)');

                        // Update Redux state
                        const connectedDevice = unifiedPrinterService.getConnectedDevice();
                        if (connectedDevice) {
                            logger.debug('Updating Redux state with unified printer', { data: { connectedDevice }, component: 'PersistentPrinterService' });
                            store.dispatch(setCurrentConnectedPrinter({
                                address: connectedDevice.address,
                                name: connectedDevice.name,
                                id: connectedDevice.id
                            }));
                            logger.debug('Redux state updated successfully', { component: 'PersistentPrinterService' });
                        }

                        return true;
                    } else {
                        logger.debug('Unified printer connection failed - not connected after connect attempt');
                    }
                } catch (error) {
                    logger.debug('Unified printer reconnection failed', { error: String(error) });
                }
            } else {
                logger.debug('Unified printer service not available');
            }

            logger.warn('Auto-reconnection failed - no printer services available');
            return false;
        } catch (error) {
            logger.error('Auto-reconnection failed', { error: String(error) });
            return false;
        }
    }

    /**
     * Start auto-reconnection with retry logic
     */
    async startAutoReconnection(): Promise<void> {
        // Check if we should attempt auto-reconnection
        logger.debug('startAutoReconnection', { component: 'PersistentPrinterService' });
        const shouldAttempt = this.shouldAttemptAutoReconnection();
        if (!shouldAttempt) {
            logger.info('Auto-reconnection not needed - no saved printer connection');
            return;
        }

        logger.info('Starting auto-reconnection process');
        this.reconnectionAttempts = 0;
        await this.retryReconnection();
    }

    /**
     * Retry reconnection with exponential backoff
     */
    private async retryReconnection(): Promise<void> {
        if (this.reconnectionAttempts >= this.maxReconnectionAttempts) {
            logger.warn('Max reconnection attempts reached, giving up');
            return;
        }

        this.reconnectionAttempts++;
        logger.info(`Reconnection attempt ${this.reconnectionAttempts}/${this.maxReconnectionAttempts}`);

        const success = await this.attemptAutoReconnection();
        if (success) {
            logger.info('Auto-reconnection successful');
            this.reconnectionAttempts = 0;
            return;
        }

        // Wait before next attempt with exponential backoff
        const delay = this.reconnectionDelay * Math.pow(2, this.reconnectionAttempts - 1);
        logger.info(`Waiting ${delay}ms before next reconnection attempt`);

        setTimeout(() => {
            this.retryReconnection();
        }, delay);
    }

    /**
     * Clear saved printer connection
     */
    clearSavedConnection(): void {
        try {
            logger.info('Clearing saved printer connection');
            this.removeItem(STORAGE_KEYS.LAST_PRINTER);
            logger.info('Saved printer connection cleared successfully');
        } catch (error) {
            logger.error('Failed to clear saved printer connection', { error: String(error) });
        }
    }

    /**
     * Get connection history (for future enhancement)
     */
    getConnectionHistory(): PrinterConnectionInfo[] {
        // For now, just return the last connected printer
        const lastPrinter = this.getSavedPrinterConnection();
        return lastPrinter ? [lastPrinter] : [];
    }

    /**
     * Get unified printer service dynamically
     */
    private async getUnifiedPrinterService() {
        try {
            const { unifiedPrinterService } = await import('./unified-printer.service');
            return unifiedPrinterService;
        } catch (error) {
            logger.debug('Unified printer service not available', {
                error: String(error),
                message: error instanceof Error ? error.message : String(error),
                name: error instanceof Error ? error.name : undefined
            });
            return null;
        }
    }
}

export const persistentPrinterService = new PersistentPrinterService(); 